<script setup lang="ts">
import {
  EnrichmentFieldState,
  type EnrichmentRunSummary,
  type EnrichmentStatusEntry,
  type RefreshOutcomeDto,
} from '@factoryfixinc/ats-interfaces';
import { computed, getCurrentInstance, onBeforeUnmount, onMounted, ref, watch } from 'vue';

import EnrichmentCheckingPill from '@/components/Common/EnrichmentCheckingPill.vue';
import EnrichmentChip from '@/components/Common/EnrichmentChip.vue';
import EnrichmentTracePanel from '@/components/Common/EnrichmentTracePanel.vue';

interface Props {
  outcome: RefreshOutcomeDto | null;
  fieldStates?: EnrichmentStatusEntry['fields'];
  result?: EnrichmentRunSummary | null;
  reducedMotion?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  fieldStates: undefined,
  result: null,
  reducedMotion: false,
});

const emit = defineEmits<{
  (e: 'click', result: EnrichmentRunSummary): void;
  (e: 'resolved'): void;
}>();

type ProgressPhase = 'hidden' | 'checking' | 'finalizing' | 'terminal';

const MINIMUM_CHECKING_DURATION_MS = 1500;
const FINALE_DURATION_MS = 1200;
// Keep the receipt below the Talent Search sticky chrome (z-10 results bars, z-20 header) while still clearing normal page content.
const TRACE_PANEL_Z_INDEX = 9;

const phase = ref<ProgressPhase>('hidden');
const terminalOutcome = ref<RefreshOutcomeDto | null>(null);
const isTracePanelOpen = ref(false);
const celebrateAt = ref<number | undefined>(undefined);
// Stable id linking the loading/terminal chip to its anchored panel across instances.
const tracePanelId = `enrichment-trace-panel-${getCurrentInstance()?.uid ?? 0}`;
// Vuetify's #activator slot needs its own ref on the slot content for positioning and
// clobbers ours, so the activator can only be reached by querying the DOM.
const activatorHost = ref<HTMLElement | null>(null);
const checkingMessage = ref('');
const systemPrefersReducedMotion = ref(false);
const prefersReducedMotion = computed(
  () => props.reducedMotion || systemPrefersReducedMotion.value,
);
let checkingStartedAt: number | null = null;
let finaleStartTimeoutId: number | null = null;
let terminalTimeoutId: number | null = null;
let reducedMotionQuery: MediaQueryList | null = null;

const fields = computed(() => [
  {
    key: 'phone',
    label: 'Phone',
    state: props.fieldStates?.phone ?? EnrichmentFieldState.CHECKING,
  },
  {
    key: 'email',
    label: 'Email',
    state: props.fieldStates?.email ?? EnrichmentFieldState.CHECKING,
  },
  {
    key: 'work-history',
    label: 'History',
    state: props.fieldStates?.workHistory ?? EnrichmentFieldState.CHECKING,
  },
]);
function clearTransitionTimers(): void {
  if (finaleStartTimeoutId !== null) window.clearTimeout(finaleStartTimeoutId);
  if (terminalTimeoutId !== null) window.clearTimeout(terminalTimeoutId);
  finaleStartTimeoutId = null;
  terminalTimeoutId = null;
}

function showTerminalOutcome(outcome: RefreshOutcomeDto): void {
  clearTransitionTimers();
  terminalOutcome.value = outcome;
  phase.value = 'terminal';
  checkingStartedAt = null;
  celebrateAt.value = isTracePanelOpen.value ? Date.now() + 150 : undefined;
  emit('resolved');
}

function startFinale(outcome: RefreshOutcomeDto): void {
  phase.value = 'finalizing';
  terminalTimeoutId = window.setTimeout(() => showTerminalOutcome(outcome), FINALE_DURATION_MS);
}

function syncOutcome(outcome: RefreshOutcomeDto | null): void {
  clearTransitionTimers();

  if (!outcome) {
    isTracePanelOpen.value = false;
    terminalOutcome.value = null;
    phase.value = 'hidden';
    checkingStartedAt = null;
    celebrateAt.value = undefined;
    checkingMessage.value = '';
    return;
  }

  if (outcome.outcome === 'checking') {
    // `checking` right after `terminal` never happens mid-run: this instance was handed
    // a different candidate, so the previous one's open receipt must not carry over.
    if (phase.value === 'terminal') {
      isTracePanelOpen.value = false;
      checkingMessage.value = '';
      celebrateAt.value = undefined;
    }
    if (phase.value !== 'checking' && phase.value !== 'finalizing') {
      checkingStartedAt = Date.now();
    }
    terminalOutcome.value = null;
    phase.value = 'checking';
    return;
  }

  if (prefersReducedMotion.value) {
    showTerminalOutcome(outcome);
    return;
  }

  if (checkingStartedAt === null) {
    showTerminalOutcome(outcome);
    return;
  }

  const elapsedCheckingMs = Date.now() - checkingStartedAt;
  const remainingCheckingMs = Math.max(0, MINIMUM_CHECKING_DURATION_MS - elapsedCheckingMs);
  finaleStartTimeoutId = window.setTimeout(() => startFinale(outcome), remainingCheckingMs);
}

function handleClick(result: EnrichmentRunSummary | null): void {
  if (result) emit('click', result);
}

function handleCheckingMessage(message: string): void {
  checkingMessage.value = message;
}

function isActivatorPhase(candidate: ProgressPhase): boolean {
  return candidate === 'checking' || candidate === 'finalizing';
}

function currentActivatorEl(): HTMLElement | null {
  return activatorHost.value?.querySelector<HTMLElement>('[aria-haspopup]') ?? null;
}

let activatorObserver: MutationObserver | null = null;

function stopWatchingForActivator(): void {
  activatorObserver?.disconnect();
  activatorObserver = null;
}

watch(() => props.outcome, syncOutcome, { immediate: true });
watch(isTracePanelOpen, (isOpen) => {
  if (isOpen && phase.value === 'terminal') {
    celebrateAt.value = Date.now() + 350;
  }
});
watch(prefersReducedMotion, (reducedMotion) => {
  if (reducedMotion && props.outcome && props.outcome.outcome !== 'checking') {
    showTerminalOutcome(props.outcome);
  }
});
// The out-in Transition doesn't mount the incoming activator until the leave finishes,
// so observe the real DOM change rather than guessing on nextTick.
watch(phase, (newPhase, oldPhase) => {
  if (oldPhase === 'hidden' || oldPhase === newPhase) return;
  if (isActivatorPhase(newPhase) === isActivatorPhase(oldPhase)) return;

  const host = activatorHost.value;
  const outgoing = currentActivatorEl();
  const hadFocus = !!outgoing && outgoing.contains(document.activeElement);
  stopWatchingForActivator();
  if (!host || !hadFocus) return;

  activatorObserver = new MutationObserver(() => {
    const incoming = currentActivatorEl();
    if (!incoming || incoming === outgoing) return;
    incoming.focus();
    stopWatchingForActivator();
  });
  activatorObserver.observe(host, { childList: true, subtree: true });
});

function handleReducedMotionChange(event: MediaQueryListEvent): void {
  systemPrefersReducedMotion.value = event.matches;
}

onMounted(() => {
  reducedMotionQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)') ?? null;
  systemPrefersReducedMotion.value = reducedMotionQuery?.matches ?? false;
  reducedMotionQuery?.addEventListener?.('change', handleReducedMotionChange);
});

onBeforeUnmount(() => {
  clearTransitionTimers();
  stopWatchingForActivator();
  reducedMotionQuery?.removeEventListener?.('change', handleReducedMotionChange);
});
</script>

<template>
  <div
    v-if="phase !== 'hidden'"
    data-test-id="enrichment-progress"
    class="inline-flex min-w-0 max-w-full items-center"
  >
    <template v-if="phase === 'checking' || phase === 'finalizing'">
      <span class="sr-only" role="status" aria-live="polite">{{ checkingMessage }}</span>
      <span class="sr-only">
        <span
          v-for="field in fields"
          :key="field.key"
          :data-test-id="`enrichment-progress-${field.key}`"
          :data-state="field.state"
        >
          {{ field.label }}: {{ field.state }}
        </span>
      </span>
    </template>

    <v-menu
      v-model="isTracePanelOpen"
      :close-on-content-click="false"
      transition="enrichment-trace-transition"
      location="bottom start"
      :offset="8"
      :z-index="TRACE_PANEL_Z_INDEX"
      :activator-props="{
        'aria-haspopup': 'dialog',
        'aria-controls': tracePanelId,
        'aria-owns': tracePanelId,
      }"
    >
      <template #activator="{ props: activatorProps }">
        <div ref="activatorHost" class="contents">
          <Transition name="enrichment-progress-collapse" mode="out-in">
            <EnrichmentCheckingPill
              v-if="phase === 'checking' || phase === 'finalizing'"
              v-bind="activatorProps"
              key="checking"
              :phase="phase"
              :reduced-motion="prefersReducedMotion"
              @message="handleCheckingMessage"
            />
            <EnrichmentChip
              v-else-if="terminalOutcome"
              v-bind="activatorProps"
              key="terminal"
              :outcome="terminalOutcome"
              :result="result"
              :field-states="fieldStates"
              @select="handleClick"
            />
          </Transition>
        </div>
      </template>

      <EnrichmentTracePanel
        :id="tracePanelId"
        :outcome="terminalOutcome ?? outcome"
        :field-states="fieldStates"
        :result="result"
        :is-loading="phase === 'checking' || phase === 'finalizing'"
        :celebrate-at="celebrateAt"
        @close="isTracePanelOpen = false"
      />
    </v-menu>
  </div>
</template>

<style scoped>
.enrichment-progress-collapse-enter-active,
.enrichment-progress-collapse-leave-active {
  transition:
    opacity 450ms cubic-bezier(0.2, 0.8, 0.2, 1),
    transform 450ms cubic-bezier(0.2, 0.8, 0.2, 1),
    max-width 450ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

.enrichment-progress-collapse-enter-from,
.enrichment-progress-collapse-leave-to {
  max-width: 0;
  opacity: 0;
  transform: scale(0.85);
}

@media (prefers-reduced-motion: reduce) {
  .enrichment-progress-collapse-enter-active,
  .enrichment-progress-collapse-leave-active {
    transition: none;
  }
}
</style>

<style>
.enrichment-trace-transition-enter-active,
.enrichment-trace-transition-leave-active {
  transition:
    opacity 240ms cubic-bezier(0.2, 0.8, 0.2, 1),
    transform 240ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

.enrichment-trace-transition-enter-from,
.enrichment-trace-transition-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

@media (prefers-reduced-motion: reduce) {
  .enrichment-trace-transition-enter-active,
  .enrichment-trace-transition-leave-active {
    transition: none;
  }
}
</style>
